/**
 * EnumMultiSelect Component
 *
 * Multi-select dropdown for enum fields with allowed_values.
 * Used for operators: IN, NOT_IN (when field has small allowed_values list)
 *
 * NOTE: This is used for fields with <100 predefined options.
 * For large lists or free-form values, use PasteListInput instead.
 *
 * When useDynamicValues is enabled, fetches values from /distinct endpoint
 * and displays a truncation warning if results were limited.
 */

import { Select, Radio, Space, Typography, Spin } from 'antd'
import { useState, useEffect, useMemo } from 'react'
import type { FieldMetadata } from '@/services/metadataService'
import { apiService } from '@/services/apiService'
import { PasteListInput } from './PasteListInput'
import type { Filter } from '@/types/api.types'

const { Text } = Typography

interface EnumMultiSelectProps {
  value?: string[]
  onChange: (values: string[]) => void
  fieldMetadata: FieldMetadata
  fieldType: 'string' | 'uuid' | 'numeric'
  fieldName: string
  placeholder?: string
  /** API endpoint to fetch distinct values from (e.g., '/offerings') */
  endpoint?: string
  /** Whether to fetch values dynamically from /distinct endpoint */
  useDynamicValues?: boolean
  /** Base filters to apply when fetching distinct values */
  baseFilters?: Filter[]
  /** Whether the table has lifecycle states (to filter out superseded/expired) */
  hasLifecycle?: boolean
}

export function EnumMultiSelect({
  value,
  onChange,
  fieldMetadata,
  fieldType,
  fieldName,
  placeholder,
  endpoint,
  useDynamicValues = false,
  baseFilters = [],
  hasLifecycle = false,
}: EnumMultiSelectProps) {
  const staticAllowedValues = fieldMetadata.allowed_values || []

  // Dynamic values state (from /distinct endpoint)
  const [dynamicOptions, setDynamicOptions] = useState<string[]>([])
  const [truncated, setTruncated] = useState(false)
  const [limit, setLimit] = useState<number | undefined>()
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  // Build filters for distinct endpoint:
  // 1. Include base filters from table
  // 2. Always exclude soft-deleted records
  // 3. For lifecycle tables, exclude superseded and expired records
  const distinctFilters = useMemo(() => {
    const filters: Filter[] = [...baseFilters]

    // Always exclude soft-deleted records (unless already filtered)
    const hasDeletedFilter = filters.some(f => f.key === 'deletedAt')
    if (!hasDeletedFilter) {
      filters.push({ key: 'deletedAt', constraint_type: 'IS_NULL' })
    }

    // For lifecycle tables, only include draft and active records
    if (hasLifecycle) {
      const hasLifecycleFilter = filters.some(f => f.key === 'lifecycleState')
      if (!hasLifecycleFilter) {
        filters.push({
          key: 'lifecycleState',
          constraint_type: 'IN',
          values: ['draft', 'active'],
        })
      }
    }

    return filters
  }, [baseFilters, hasLifecycle])

  // Fetch dynamic values from /distinct endpoint
  useEffect(() => {
    if (!useDynamicValues || !endpoint) {
      return
    }

    const fetchDistinctValues = async () => {
      setLoading(true)
      setFetchError(null)
      try {
        // Build URL with filters query parameter
        const filtersParam = distinctFilters.length > 0
          ? `?filters=${encodeURIComponent(JSON.stringify(distinctFilters))}`
          : ''
        const response = await apiService.get<string[]>(`${endpoint}/distinct/${fieldName}${filtersParam}`)
        if (response.success && response.data) {
          setDynamicOptions(response.data)
          setTruncated(response.meta?.truncated ?? false)
          setLimit(response.meta?.limit)
        } else {
          setFetchError(response.errors?.[0]?.message || 'Failed to fetch values')
        }
      } catch {
        setFetchError('Failed to fetch values')
      } finally {
        setLoading(false)
      }
    }

    fetchDistinctValues()
  }, [useDynamicValues, endpoint, fieldName, distinctFilters])

  // Determine which values to use
  const allowedValues = useDynamicValues && endpoint ? dynamicOptions : staticAllowedValues
  const hasAllowedValues = allowedValues.length > 0
  const hasManyOptions = allowedValues.length > 20

  // Show mode toggle only if field has many options (>20)
  const [inputMode, setInputMode] = useState<'select' | 'paste'>(
    hasManyOptions ? 'paste' : 'select'
  )

  // Loading state for dynamic values
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '8px' }}>
        <Spin size="small" />
        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
          Loading values...
        </Text>
      </div>
    )
  }

  // Error state for dynamic values
  if (fetchError) {
    return (
      <Text type="danger" style={{ fontSize: 12 }}>
        {fetchError}
      </Text>
    )
  }

  // If field has allowed_values but only a few, show dropdown only
  if (hasAllowedValues && !hasManyOptions) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Select
          mode="multiple"
          value={value}
          onChange={onChange}
          placeholder={placeholder || 'Select values'}
          style={{ width: '100%' }}
          showSearch
          maxTagCount="responsive"
          options={allowedValues
            .filter((option) => option !== null && option !== undefined)
            .map((option) => ({
              label: typeof option === 'string' ? option : option.label || option.value,
              value: typeof option === 'string' ? option : option.value,
            }))}
        />
        {/* Truncation warning for dynamic values */}
        {useDynamicValues && truncated && (
          <Text type="warning" style={{ fontSize: 12 }}>
            Showing first {limit || 250} values. Type to search for more.
          </Text>
        )}
      </Space>
    )
  }

  // Field has many options or no allowed_values - show mode toggle
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      {/* Mode toggle */}
      {hasManyOptions && (
        <Radio.Group
          value={inputMode}
          onChange={(e) => setInputMode(e.target.value)}
          size="small"
          style={{ marginBottom: 4 }}
        >
          <Radio.Button value="select">Select from list</Radio.Button>
          <Radio.Button value="paste">Paste list</Radio.Button>
        </Radio.Group>
      )}

      {/* Input based on mode */}
      {inputMode === 'select' && hasAllowedValues ? (
        <>
          <Select
            mode="multiple"
            value={value}
            onChange={onChange}
            placeholder={placeholder || 'Select values'}
            style={{ width: '100%' }}
            showSearch
            maxTagCount="responsive"
            options={allowedValues.map((option) => ({
              label: typeof option === 'string' ? option : option.label || option.value,
              value: typeof option === 'string' ? option : option.value,
            }))}
          />
          {/* Truncation warning for dynamic values */}
          {useDynamicValues && truncated && (
            <Text type="warning" style={{ fontSize: 12 }}>
              Showing first {limit || 250} values. Type to search for more.
            </Text>
          )}
        </>
      ) : (
        <PasteListInput
          value={value || []}
          onChange={onChange}
          fieldType={fieldType}
          fieldName={fieldName}
          placeholder={placeholder}
        />
      )}
    </Space>
  )
}
